import { useEffect, useState } from "react";
import PropTypes from "prop-types";
import {
  Modal,
  Button,
  Form,
  Col,
  Row,
  Alert,
  Spinner,
  ProgressBar,
} from "react-bootstrap";
import api from "@/api/api";
import { ROLES } from "@/config/roles.js";

const ROLE_LABELS = {
  [ROLES.USER]: "Usuario",
  [ROLES.ADMIN]: "Contador",
  [ROLES.SUPER_ADMIN]: "Administrador",
};

const initialFormData = {
  id: "",
  username: "",
  role: "",
  password: "",
  userProfile: {
    name: "",
    lastname: "",
    userType: "contador",
  },
};

const UserFormModal = ({ show, handleClose, selectedUserId, onSave }) => {
  const [formData, setFormData] = useState(initialFormData);
  const [confirmPassword, setConfirmPassword] = useState("");
  const [passwordStrength, setPasswordStrength] = useState(0);
  const [passwordMatch, setPasswordMatch] = useState(true);
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (show && selectedUserId) {
      setLoading(true);
      api
        .get(`/users/${selectedUserId}`)
        .then((response) => {
          const data = response.data;
          setFormData({
            id: data.id,
            username: data.username,
            // Assuming the API returns an array of roles, we take the first one for the radio button
            role: data.roles && data.roles.length > 0 ? data.roles[0] : "",
            password: "", // We don't populate password for security
            userProfile: {
              name: data.userProfile?.name || "",
              lastname: data.userProfile?.lastname || "",
              userType: data.userProfile?.userType || "contador",
            },
          });
          setLoading(false);
        })
        .catch((error) => {
          console.error("Error fetching user:", error);
          setErrors({ general: "Error al cargar los datos del usuario." });
          setLoading(false);
        });
    } else if (show) {
      // Reset form when opening in "Create" mode (no selectedUserId)
      resetForm();
    }
  }, [show, selectedUserId]);

  // Función para validar formato de email
  const isValidEmail = (email) => {
    return /^\S+@\S+\.\S+$/.test(email);
  };

  // Calcula la fortaleza de la contraseña (máximo 100)
  const calculatePasswordStrength = (password) => {
    let strength = 0;
    if (password.length >= 6) strength += 20;
    if (/[A-Z]/.test(password)) strength += 20;
    if (/[a-z]/.test(password)) strength += 20;
    if (/\d/.test(password)) strength += 20;
    if (/[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?]+/.test(password)) strength += 20;
    return strength;
  };

  const handlePasswordChange = (e) => {
    const newPassword = e.target.value;
    setFormData((prev) => ({ ...prev, password: newPassword }));
    const strength = calculatePasswordStrength(newPassword);
    setPasswordStrength(strength);
    if (confirmPassword !== "") {
      setPasswordMatch(newPassword === confirmPassword);
    }
  };

  const handleConfirmPasswordChange = (e) => {
    const newConfirm = e.target.value;
    setConfirmPassword(newConfirm);
    setPasswordMatch(formData.password === newConfirm);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name === "name" || name === "lastname") {
      setFormData((prev) => ({
        ...prev,
        userProfile: { ...prev.userProfile, [name]: value },
      }));
      if (errors[name]) setErrors((prev) => ({ ...prev, [name]: null }));
    } else {
      setFormData((prev) => ({ ...prev, [name]: value }));
      if (errors[name]) setErrors((prev) => ({ ...prev, [name]: null }));
    }
  };

  const resetForm = () => {
    setFormData(initialFormData);
    setConfirmPassword("");
    setPasswordStrength(0);
    setPasswordMatch(true);
    setErrors({});
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const newErrors = {};

    if (!isValidEmail(formData.username)) {
      newErrors.username = "El correo no es válido";
    }
    if (!formData.userProfile.name.trim()) {
      newErrors.name = "El nombre es obligatorio";
    }
    if (!formData.userProfile.lastname.trim()) {
      newErrors.lastname = "El apellido es obligatorio";
    }
    if (!formData.role) {
      newErrors.role = "Debes seleccionar un rol";
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors((prev) => ({ ...prev, ...newErrors }));
      return;
    }

    const isEditing = !!formData.id;
    const passwordChanged = formData.password.length > 0;

    // Validation logic: Only check password if creating OR if editing and password is provided
    if (
      (!isEditing || passwordChanged) &&
      (!passwordMatch || passwordStrength <= 30)
    ) {
      setErrors((prev) => ({
        ...prev,
        general: "La contraseña no cumple con los requisitos.",
      }));
      return;
    }

    setLoading(true);
    setErrors({});

    const roles =
      formData.role === ROLES.SUPER_ADMIN
        ? [ROLES.ADMIN, ROLES.SUPER_ADMIN]
        : formData.role
          ? [formData.role]
          : [];

    const userPayload = {
      username: formData.username,
      roles,
      userProfile: {
        name: formData.userProfile.name,
        lastname: formData.userProfile.lastname,
        userType: formData.userProfile.userType,
      },
    };

    // Only add password to payload if it is a new user or if the password field was changed
    if (!isEditing || passwordChanged) {
      userPayload.password = formData.password;
    }

    try {
      if (isEditing) {
        // Update existing user
        await api.patch(`/users/${formData.id}`, userPayload, {
          headers: { "Content-Type": "application/merge-patch+json" },
        });
      } else {
        // Create new user
        await api.post("/users", userPayload, {
          headers: { "Content-Type": "application/ld+json" },
        });
      }

      setLoading(false);
      resetForm();
      handleClose();
      if (onSave) onSave();
    } catch (err) {
      setLoading(false);
      if (err.response?.data?.violations) {
        const validationErrors = {};
        err.response.data.violations.forEach((violation) => {
          validationErrors[violation.propertyPath] = violation.message;
        });
        setErrors(validationErrors);
      } else {
        setErrors({
          general:
            "Error inesperado, verifica la conexión o intenta nuevamente.",
        });
      }
    }
  };

  return (
    <Modal show={show} onHide={handleClose}>
      <Modal.Header closeButton>
        <Modal.Title>
          {formData.id ? "Editar Usuario" : "Crear Usuario"}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {errors.general && <Alert variant="danger">{errors.general}</Alert>}
        <Form onSubmit={handleSubmit} noValidate>
          <Form.Control type="hidden" name="id" value={formData.id} />
          <Form.Group className="mb-3">
            <Form.Label>Correo del usuario</Form.Label>
            <Form.Control
              type="email"
              name="username"
              placeholder="Ingresa el correo con el que se loggeará el usuario"
              value={formData.username}
              onChange={(e) =>
                setFormData({ ...formData, username: e.target.value })
              }
              isInvalid={!!errors.username}
            />
            {errors.username && (
              <Form.Control.Feedback type="invalid">
                {errors.username}
              </Form.Control.Feedback>
            )}
          </Form.Group>

          <Row>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Nombre</Form.Label>
                <Form.Control
                  type="text"
                  name="name"
                  placeholder="Ingresa el nombre"
                  value={formData.userProfile.name}
                  onChange={handleChange}
                  isInvalid={!!errors.name}
                />
                {errors.name && (
                  <Form.Control.Feedback type="invalid">
                    {errors.name}
                  </Form.Control.Feedback>
                )}
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Apellido</Form.Label>
                <Form.Control
                  type="text"
                  name="lastname"
                  placeholder="Ingresa el apellido"
                  value={formData.userProfile.lastname}
                  onChange={handleChange}
                  isInvalid={!!errors.lastname}
                />
                {errors.lastname && (
                  <Form.Control.Feedback type="invalid">
                    {errors.lastname}
                  </Form.Control.Feedback>
                )}
              </Form.Group>
            </Col>
          </Row>

          <Row className="mb-3">
            <Col sm={6}>
              <Form.Group controlId="password">
                <Form.Label>Contraseña nueva</Form.Label>
                <Form.Control
                  type="password"
                  placeholder="Ingresa tu nueva contraseña"
                  value={formData.password}
                  onChange={handlePasswordChange}
                  isInvalid={!!errors.password}
                />
                <ProgressBar
                  now={passwordStrength}
                  label={`${passwordStrength}%`}
                  className="mt-2"
                />
                <Form.Text className="text-muted">
                  La contraseña debe contener al menos 6 caracteres, una letra
                  mayúscula, una letra minúscula, un número y un carácter
                  especial.
                </Form.Text>
              </Form.Group>
            </Col>
            <Col sm={6}>
              <Form.Group controlId="confirmPassword">
                <Form.Label>Confirmar contraseña</Form.Label>
                <Form.Control
                  type="password"
                  placeholder="Confirma tu nueva contraseña"
                  value={confirmPassword}
                  onChange={handleConfirmPasswordChange}
                />
              </Form.Group>
            </Col>
          </Row>
          {!passwordMatch && (
            <Alert variant="danger">Las contraseñas no coinciden</Alert>
          )}

          <Form.Group className="mb-3">
            <Form.Label>Roles</Form.Label>
            {Object.entries(ROLE_LABELS)
              .filter(([roleValue]) => roleValue !== ROLES.USER)
              .map(([roleValue, roleLabel]) => (
              <Form.Check
                key={roleValue}
                type="radio"
                label={roleLabel}
                name="role"
                value={roleValue}
                checked={formData.role === roleValue}
                onChange={(e) => {
                  setFormData({ ...formData, role: e.target.value });
                  if (errors.role)
                    setErrors((prev) => ({ ...prev, role: null }));
                }}
              />
            ))}
            {errors.role && <Alert variant="danger">{errors.role}</Alert>}
          </Form.Group>

          <Button
            variant="primary"
            type="submit"
            disabled={
              loading ||
              // Disable if:
              // 1. Creating new user AND (passwords don't match OR weak)
              // 2. Editing user AND password field is filled AND (passwords don't match OR weak)
              ((!formData.id || formData.password.length > 0) &&
                (!passwordMatch || passwordStrength <= 30))
            }
          >
            {loading ? (
              <Spinner animation="border" size="sm" />
            ) : (
              "Guardar Usuario"
            )}
          </Button>
        </Form>
      </Modal.Body>
    </Modal>
  );
};
UserFormModal.propTypes = {
  show: PropTypes.bool.isRequired,
  handleClose: PropTypes.func.isRequired,
  selectedUserId: PropTypes.string,
  onSave: PropTypes.func.isRequired,
};
export default UserFormModal;
